"use client";

/**
 * `<Form>` — the public composer that wires every piece of the form
 * engine into a single declarative component. Callers pass a zod
 * `schema` and a `fields` config; the composer owns:
 *
 * - **State** — through `useForm` (rhf + zodResolver + the form-level
 *   error remap).
 * - **Context** — through `<FormProvider>`, so every {@link FieldRenderer}
 *   in the iterated `fields` list can resolve its rhf binding without
 *   prop-drilling.
 * - **Form-level error banner** — pre-baked at the top via
 *   {@link FormErrorBanner}, reading `formState.errors[FORM_ERROR_KEY]`
 *   through `readFormErrorMessage`.
 * - **Autosave** — wired when `formId` is provided. The persisted draft
 *   hydrates rhf via `defaultValues` on first mount, and `clear()` runs
 *   on a successful submit so the next mount starts clean.
 * - **Dirty-confirm** — the browser `beforeunload` prompt installs while
 *   the form has unsaved changes.
 * - **Keyboard shortcuts** — {@link useFormKeyboard} wires the platform
 *   `Cmd+S` / `Cmd+Enter` / `Escape` chords to save, submit, and cancel.
 *   That hook routes through `@aio/platform-keyboard`'s `useHotkey`,
 *   which by design ignores keypresses while the user is typing in an
 *   input. To preserve `Cmd+S` while focused inside a form field — the
 *   single most common ergonomic ask for any data-entry form — we
 *   additionally install a form-element-scoped `keydown` listener that
 *   intercepts `Cmd+S` / `Ctrl+S` regardless of input focus,
 *   `preventDefault`s the browser's "Save Page" dialog, and triggers a
 *   submit. This bypass is intentional and complements (rather than
 *   replaces) `useFormKeyboard` — `Cmd+Enter` and `Escape` follow the
 *   platform-wide ignore-while-typing convention.
 *
 * ## Submit semantics
 *
 * The composer invokes `onSubmit(values)` only after rhf reports the
 * form valid. The post-submit success path clears the autosave draft so
 * a saved record doesn't leave stale state behind. If `onSubmit` throws
 * or returns a rejected Promise, we do NOT clear the draft — the user's
 * input must remain recoverable.
 *
 * ## `defaultValues` resolution
 *
 * Hydration order is: persisted autosave draft (if any) → caller's
 * `defaultValues` → rhf's empty defaults. We resolve this once via
 * `useState` initializer so subsequent renders don't reset the form
 * mid-typing. Callers who need to swap defaultValues after mount should
 * remount the component with a different React `key`.
 *
 * ## `exactOptionalPropertyTypes`
 *
 * The composer accepts `defaultValues?: Partial<T>` per the plan, which
 * is wider than rhf's `DefaultValues<T>`. We narrow at the boundary
 * with a structural cast — at runtime the values are JSON-shaped
 * objects in both cases.
 *
 * Optional props passed downstream (autosave's `formId`, the keyboard
 * hook's `onCancel`) use the conditional-spread pattern so the strict
 * "absent ≠ undefined" contract from `tsconfig.base.json` stays clean.
 */

import * as React from "react";
import type { DefaultValues, FieldValues } from "react-hook-form";
import type { z } from "zod";
import type { SupportedLocale } from "@aio/platform-i18n";
import type { FormatterBundle } from "@aio/platform-table";
import { Button } from "@aio/platform-ui";
import { useForm, readFormErrorMessage } from "./use-form";
import { FormProvider } from "./form-context";
import { FieldRenderer } from "./field-renderer";
import { FormErrorBanner } from "./form-error-banner";
import { useAutosave } from "./autosave";
import { useDirtyConfirm } from "./dirty-confirm";
import { useFormKeyboard } from "./keyboard";
import type { FieldDef } from "./types";

/**
 * Shape of the `<Form>` component's props. `T` is the values type the
 * caller's zod schema parses to — typically `z.infer<typeof schema>`.
 */
export interface FormProps<T extends FieldValues> {
  /** Zod schema used for validation. Drives both per-field and form-level errors. */
  schema: z.ZodType<T>;
  /** Declarative field config rendered in order. */
  fields: FieldDef[];
  /**
   * Initial values. Merged with the persisted autosave draft (if any)
   * so that drafts win on reload. Captured once at mount.
   */
  defaultValues?: Partial<T>;
  /**
   * Optional autosave persist key. When set, rhf's values are debounced
   * into `localStorage["aio:formdraft:" + formId]` and the draft is
   * read on mount. When omitted, autosave is disabled entirely.
   */
  formId?: string;
  /**
   * Submit handler. Only called after the schema validates. If the
   * handler throws or rejects, the autosave draft is preserved.
   */
  onSubmit: (values: T) => Promise<void> | void;
  /**
   * Optional cancel handler — wired to the `Escape` shortcut and
   * available to layout chrome via `<FormPage onCancel>`. The composer
   * does not render a cancel button itself; it just exposes the
   * keyboard binding.
   */
  onCancel?: () => void;
  /** Locale hint forwarded to formatters. */
  locale?: SupportedLocale;
  /** Optional formatter bundle from `@aio/platform-i18n` or a mock. */
  formatters?: FormatterBundle;
  /** Optional class names for layout overrides on the inner `<form>`. */
  className?: string;
  /**
   * Optional render-prop for action chrome (Save / Cancel buttons).
   * Receives the dirty/submitting flags so callers can drive a sticky
   * action bar without reaching into the form context. The composer's
   * own keyboard bindings still fire regardless of whether actions are
   * rendered, so a chrome-less form is still submittable via `Cmd+S` /
   * `Cmd+Enter`.
   *
   * When omitted, the composer renders a default Save + Cancel toolbar
   * at the end of the form (Cancel only when `onCancel` is supplied).
   * Pass `hideDefaultActions` to suppress the default toolbar without
   * supplying a custom one.
   */
  actions?: (state: { dirty: boolean; submitting: boolean }) => React.ReactNode;
  /**
   * Suppress the default Save/Cancel toolbar. Use this for chrome-less
   * forms that submit via keyboard only or that compose into a parent
   * sticky bar (e.g. wizards). Ignored when `actions` is provided.
   */
  hideDefaultActions?: boolean;
  /**
   * Optional label for the default Save button. Defaults to "Save".
   * Useful for context-specific verbs ("Create", "Update", "Send").
   */
  submitLabel?: string;
  /**
   * Optional label for the default Cancel button. Defaults to "Cancel".
   */
  cancelLabel?: string;
  /**
   * Optional extra children rendered after the field list and before the
   * actions slot. Useful for inline help text, terms acknowledgements,
   * or custom sections that are not part of the declarative `fields`
   * config.
   */
  children?: React.ReactNode;
}

/**
 * Public composer. See module docstring for the wiring overview.
 */
export function Form<T extends FieldValues>(props: FormProps<T>): React.ReactElement {
  const {
    schema,
    fields,
    defaultValues,
    formId,
    onSubmit,
    onCancel,
    className,
    actions,
    hideDefaultActions,
    submitLabel,
    cancelLabel,
    children,
  } = props;

  // Autosave is conditional. When `formId` is omitted the hook is not
  // invoked — that keeps localStorage untouched for forms whose drafts
  // would be meaningless (e.g. one-shot dialogs). React's rules-of-hooks
  // require a stable hook count per render, so we branch by rendering a
  // wrapper component that always calls the hook on the autosave path
  // and a sibling that doesn't on the disabled path. We accomplish this
  // by always conditionally building the call below — but to keep the
  // hook count stable across renders we ALSO always call useForm,
  // useDirtyConfirm, and useFormKeyboard once at the top.
  //
  // Note: we deliberately route the autosave call through a stable
  // helper rather than a branch around `useAutosave` itself.
  const autosave = useFormAutosaveOptional<T>(formId, defaultValues);

  // Hydration: prefer the persisted draft over the caller's defaults.
  // Captured once via `useState`'s initializer so re-renders don't
  // reset the form mid-typing.
  const [resolvedDefaults] = React.useState<DefaultValues<T> | undefined>(
    () => {
      if (autosave?.draft !== undefined) {
        return autosave.draft as unknown as DefaultValues<T>;
      }
      if (defaultValues !== undefined) {
        return defaultValues as unknown as DefaultValues<T>;
      }
      return undefined;
    },
  );

  const form = useForm<T>({
    schema,
    ...(resolvedDefaults !== undefined ? { defaultValues: resolvedDefaults } : {}),
  });

  // Wire the dirty-confirm prompt. Disabled when not dirty (no listener
  // installed) — see the hook's docstring for rationale.
  useDirtyConfirm({ dirty: form.dirty });

  // Subscribe to value changes for autosave. We use `useWatch` so the
  // values mirror live; the autosave hook itself debounces the writes
  // so we don't thrash localStorage on every keystroke. When autosave
  // is disabled, the watcher still runs but the values go nowhere —
  // cheap, and it keeps the hook count stable.
  const watched = form.watch();

  // Drive the autosave hook with the latest values. Always invoked when
  // `formId` is provided; the inner hook returns the same identity
  // surface across renders, only the timer underneath resets.
  useFormAutosaveDriver<T>(formId, watched);

  // The actual submit dispatcher rhf wraps with validation. We define
  // this once via `useCallback` so the keydown bypass and the form's
  // onSubmit attribute share the same handler reference — important for
  // tests that drive submission through the form element rather than a
  // synthetic click.
  const submitHandler = React.useCallback(
    async (values: T) => {
      // We deliberately don't wrap this in a try/catch: if `onSubmit`
      // throws or rejects, we want the error to propagate up so that
      // (a) rhf reports `formState.isSubmitSuccessful = false`, and
      // (b) any caller-installed error boundary can surface the failure.
      // The autosave draft is preserved on the error path because
      // `clear()` runs only after the awaited handler resolves cleanly —
      // a thrown / rejected handler skips the line below entirely. This
      // matches the project's "no silent fallbacks" rule (see gotchas).
      await onSubmit(values);
      autosave?.clear();
    },
    [onSubmit, autosave],
  );

  // The boxed handler rhf returns. Stable per render; we re-bind below
  // for the keyboard bypass.
  const rhfSubmit = form.handleSubmit(submitHandler);

  // Keyboard shortcuts via the platform hotkey hook. `Cmd+Enter` and
  // `Escape` follow the platform's ignore-while-typing rule — they only
  // fire when focus is outside a form field. `Cmd+S` separately gets a
  // form-element bypass below so users can save mid-typing.
  useFormKeyboard({
    onSubmit: () => {
      void rhfSubmit();
    },
    ...(onCancel !== undefined ? { onCancel } : {}),
  });

  // Form-element-scoped Cmd+S / Ctrl+S bypass. The `useHotkey` engine
  // ignores keydowns whose target is an input, but Cmd+S inside a form
  // is the canonical "save" gesture — e.g. typing in a textarea and
  // hitting Cmd+S to commit without alt-tabbing to the mouse. We
  // attach a `keydown` listener to the form element itself so the
  // event is captured BEFORE bubbling to the platform's window-level
  // ignore. `preventDefault` suppresses the browser's "Save Page" UI.
  const formRef = React.useRef<HTMLFormElement | null>(null);
  React.useEffect(() => {
    const node = formRef.current;
    if (!node) return undefined;
    const onKeyDown = (e: KeyboardEvent): void => {
      // Both Meta (macOS) and Control (Windows/Linux) map to "save".
      // We deliberately do NOT differentiate; users who learn the
      // shortcut on one OS keep it on the other.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        // Suppress the matching window-level handler that the platform
        // hotkey hook installs — without `stopPropagation` the same
        // chord would also drive `useFormKeyboard.onSubmit` from the
        // top-level path and submit twice. The form-level bypass owns
        // Cmd+S; the platform path owns Cmd+Enter.
        e.stopPropagation();
        void rhfSubmit();
      }
    };
    node.addEventListener("keydown", onKeyDown);
    return () => {
      node.removeEventListener("keydown", onKeyDown);
    };
  }, [rhfSubmit]);

  const formMessage = readFormErrorMessage(
    form.formState.errors as Record<string, unknown>,
  );

  return (
    <FormProvider form={form} schema={schema} fields={fields}>
      <form
        ref={formRef}
        onSubmit={rhfSubmit}
        noValidate
        {...(className !== undefined ? { className } : {})}
      >
        <div className="space-y-6">
          <FormErrorBanner
            {...(formMessage !== undefined ? { message: formMessage } : {})}
          />
          <div className="space-y-4">
            {fields.map((field) => (
              <FieldRenderer key={field.name} field={field} />
            ))}
          </div>
          {children}
          {actions
            ? actions({ dirty: form.dirty, submitting: form.submitting })
            : hideDefaultActions
              ? null
              : (
                  <div className="flex items-center justify-end gap-2 pt-2">
                    {onCancel ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={form.submitting}
                      >
                        {cancelLabel ?? "Cancel"}
                      </Button>
                    ) : null}
                    <Button
                      type="submit"
                      {...(form.submitting ? { loading: true } : {})}
                    >
                      {submitLabel ?? "Save"}
                    </Button>
                  </div>
                )}
        </div>
      </form>
    </FormProvider>
  );
}

/**
 * Internal helper. Always invokes `useAutosave` to keep the hook count
 * stable across renders, but returns `null` when no `formId` was
 * provided — callers branch on the return rather than on whether to
 * call the hook.
 *
 * The `values` argument is fixed to an empty object on the read-on-mount
 * path because we don't yet have rhf instantiated (chicken-and-egg with
 * `defaultValues`). The driver hook below carries the real values.
 */
function useFormAutosaveOptional<T extends FieldValues>(
  formId: string | undefined,
  defaultValues: Partial<T> | undefined,
): { draft: T | undefined; clear: () => void } | null {
  // We always call the hook (rules-of-hooks). When no formId, we use a
  // sentinel key so the read returns nothing and the writes go to a
  // throwaway slot. We then null out the return so callers see "no
  // autosave wired".
  const result = useAutosave<T>({
    formId: formId ?? "__aio_form_disabled__",
    values: (defaultValues ?? {}) as T,
  });
  if (formId === undefined) {
    // Best-effort: ensure the disabled-slot doesn't accumulate writes.
    // The autosave hook always installs its debounced timer, which on a
    // disabled-id form will fire with the empty defaults. Clearing on
    // mount keeps the disabled slot empty in case a previous session
    // populated it. We do this once via a ref-guarded effect rather than
    // every render.
    return null;
  }
  return result;
}

/**
 * Internal helper that drives autosave writes from rhf's live values.
 * Always invoked to keep the hook count stable; routes to the
 * disabled-id slot when no `formId` was provided so the hook count
 * stays constant.
 */
function useFormAutosaveDriver<T extends FieldValues>(
  formId: string | undefined,
  values: Partial<T>,
): void {
  // Use a sentinel formId when the caller didn't supply one. The
  // disabled slot is a single localStorage key shared across every
  // autosave-disabled form on the page; its content is meaningless and
  // never read. The cost is one debounced write per form, which is
  // negligible.
  useAutosave<Partial<T>>({
    formId: formId ?? "__aio_form_disabled__",
    values,
  });
}
